import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  AppBar,
  Toolbar,
  Button,
  Chip,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Undo,
  Redo,
  Title,
  Fullscreen,
  FullscreenExit,
  Clear,
} from "@mui/icons-material";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { Placeholder } from '@tiptap/extensions'

function ToolBtn({
  title,
  onClick,
  active,
  disabled,
  children,
}: {
  title: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: active ? "primary.main" : "divider",
            bgcolor: active ? "primary.50" : "background.paper",
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export function RichTextEditor({
  value,
  onChange,
  label = "รายละเอียดกิจกรรม",
}: {
  value: string;
  onChange: (html: string) => void;
  label?: string;
}) {
  const [full, setFull] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Placeholder.configure({
        placeholder: "พิมพ์รายละเอียดกิจกรรม…",
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // ✅ sync เมื่อ value เปลี่ยนจากภายนอก (โหลดข้อมูล)
  useEffect(() => {
    if (!editor) return;

    const next = value || "";
    const current = editor.getHTML();

    if (next !== current) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]);

  const wordCount = useMemo(() => {
    if (!editor) return 0;
    const text = editor.state.doc.textContent ?? "";
    const trimmed = text.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
  }, [editor, value]);

  const ToolbarRow = (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
      <ToolBtn
        title="หัวข้อใหญ่"
        onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
        active={!!editor?.isActive("heading", { level: 2 })}
        disabled={!editor}
      >
        <Title fontSize="small" />
      </ToolBtn>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <ToolBtn
        title="ตัวหนา"
        onClick={() => editor?.chain().focus().toggleBold().run()}
        active={!!editor?.isActive("bold")}
        disabled={!editor}
      >
        <FormatBold fontSize="small" />
      </ToolBtn>

      <ToolBtn
        title="ตัวเอียง"
        onClick={() => editor?.chain().focus().toggleItalic().run()}
        active={!!editor?.isActive("italic")}
        disabled={!editor}
      >
        <FormatItalic fontSize="small" />
      </ToolBtn>

      <ToolBtn
        title="ขีดเส้นใต้"
        onClick={() => editor?.chain().focus().toggleUnderline().run()}
        active={!!editor?.isActive("underline")}
        disabled={!editor}
      >
        <FormatUnderlined fontSize="small" />
      </ToolBtn>

      <ToolBtn
        title="ขีดฆ่า"
        onClick={() => editor?.chain().focus().toggleStrike().run()}
        active={!!editor?.isActive("strike")}
        disabled={!editor}
      >
        <StrikethroughS fontSize="small" />
      </ToolBtn>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <ToolBtn
        title="Bullet list"
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
        active={!!editor?.isActive("bulletList")}
        disabled={!editor}
      >
        <FormatListBulleted fontSize="small" />
      </ToolBtn>

      <ToolBtn
        title="Numbered list"
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        active={!!editor?.isActive("orderedList")}
        disabled={!editor}
      >
        <FormatListNumbered fontSize="small" />
      </ToolBtn>

      <ToolBtn
        title="Quote"
        onClick={() => editor?.chain().focus().toggleBlockquote().run()}
        active={!!editor?.isActive("blockquote")}
        disabled={!editor}
      >
        <FormatQuote fontSize="small" />
      </ToolBtn>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <ToolBtn
        title="Undo"
        onClick={() => editor?.chain().focus().undo().run()}
        disabled={!editor || !editor.can().undo()}
      >
        <Undo fontSize="small" />
      </ToolBtn>

      <ToolBtn
        title="Redo"
        onClick={() => editor?.chain().focus().redo().run()}
        disabled={!editor || !editor.can().redo()}
      >
        <Redo fontSize="small" />
      </ToolBtn>

      <ToolBtn
        title="ล้างรูปแบบ"
        onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()}
        disabled={!editor}
      >
        <Clear fontSize="small" />
      </ToolBtn>

      <Box sx={{ flex: 1 }} />

      <Tooltip title={full ? "ออกจากเต็มจอ" : "เต็มจอ"}>
        <span>
          <IconButton
            size="small"
            onClick={() => setFull((x) => !x)}
            disabled={!editor}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            {full ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const EditorBox = (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      {/* Toolbar */}
      <Box sx={{ p: 1.25, bgcolor: "background.default" }}>{ToolbarRow}</Box>
      <Divider />

      {/* Editor */}
      <Box
        sx={{
          p: 1.5,
          minHeight: 160,
          "& .ProseMirror": {
            outline: "none",
            minHeight: 140,
            lineHeight: 1.65,
          },
          "& .ProseMirror p": { m: 0 },
          "& .ProseMirror ul, & .ProseMirror ol": { paddingLeft: 3, margin: 0 },
          "& .ProseMirror blockquote": {
            margin: 0,
            paddingLeft: 12,
            borderLeft: "4px solid",
            borderLeftColor: "divider",
            color: "text.secondary",
          },
          "& .ProseMirror h2": { margin: 0, fontSize: "1.1rem" },
          "& .is-empty:first-of-type::before": {
            content: "attr(data-placeholder)",
            float: "left",
            color: "text.disabled",
            pointerEvents: "none",
            height: 0,
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {/* Footer */}
      <Divider />
      <Box sx={{ px: 1.5, py: 1, display: "flex", justifyContent: "space-between" }}>
        <Chip size="small" variant="outlined" label={`${wordCount} คำ`} sx={{ borderRadius: 2 }} />
      </Box>
    </Box>
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography fontWeight={800}>{label}</Typography>
      </Stack>

      {/* ปกติ */}
      {!full && EditorBox}

      {/* เต็มจอ */}
      <Dialog fullScreen open={full} onClose={() => setFull(false)}>
        <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
          <Toolbar>
            <Typography fontWeight={800} sx={{ flex: 1 }}>
              {label}
            </Typography>
            <Button onClick={() => setFull(false)} variant="contained" sx={{ borderRadius: 2 }}>
              เสร็จสิ้น
            </Button>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 2 }}>{EditorBox}</Box>
      </Dialog>
    </Box>
  );
}
